import React, { useState } from 'react';
import { apiClient } from '../../api';
import { useApp } from '../../context/AppContext';

interface ConfirmStepProps {
  protocol: 'a2a' | 'ap2' | 'mcp' | 'mpx';
  channelConfig: any;
  mpxPublishConfig: {
    connectionPhrase: string;
    headline: string;
    description: string;
    expiryDate: string | null;
    noExpiry: boolean;
    limitUses: number | null;
    noLimit: boolean;
  } | null;
  onChannelCreated: (config: any) => void;
  onBack: () => void;
}

const ConfirmStep: React.FC<ConfirmStepProps> = ({
  protocol,
  channelConfig,
  mpxPublishConfig,
  onChannelCreated,
  onBack,
}) => {
  const { actions } = useApp();
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCreate = async () => {
    setCreating(true);
    setError(null);

    try {
      let createdConfig = { ...channelConfig };
      
      if (protocol === 'mpx') {
        // For MPX, we need to:
        // 1. Register the offer with the MPX tenant
        // 2. Create the channel with the offer details
        
        if (!mpxPublishConfig) {
          throw new Error('MPX publish configuration is missing');
        }

        // Step 1: Prepare offer data (generates DIDComm message, contact card, extracts mediator info)
        const prepareResponse = await apiClient.post('/mpx-tenants/prepare-offer', {
          tenant_id: channelConfig.tenant_id,
          offer_name: mpxPublishConfig.headline,
          offer_description: mpxPublishConfig.description,
        });

        // Step 2: Register the offer with MPX tenant via gateway using prepared data
        const registerResponse = await apiClient.post('/mpx-tenants/register-offer', {
          tenant_id: channelConfig.tenant_id,
          offer_name: mpxPublishConfig.headline,
          offer_description: mpxPublishConfig.description,
          offer_type: 1, // 1 = Chat type
          didcomm_message: prepareResponse.data.didcomm_message,
          contact_card: prepareResponse.data.contact_card,
          valid_until: mpxPublishConfig.noExpiry ? null : (mpxPublishConfig.expiryDate || null),
          maximum_usage: mpxPublishConfig.noLimit ? null : (mpxPublishConfig.limitUses || null),
          platform_type: 'DIDCOMM',
          contact_attributes: 0,
          mediator_did: prepareResponse.data.mediator_did,
          mediator_endpoint: prepareResponse.data.mediator_endpoint,
          mediator_wss_endpoint: prepareResponse.data.mediator_wss_endpoint,
          custom_phrase: mpxPublishConfig.connectionPhrase,
          is_searchable: false,
          metadata: null,
        });

        // Create MPX channel with the offer details (same as other protocols)
        // MPX channels use regular HTTP listeners like other channels
        const result = await apiClient.createChannel({
          ...channelConfig,
          protocol: 'mpx',
          // Use the regular listen_address and route from channelConfig (selected by user)
          enforce_extension: false,
          enforce_response_extension: false,
          mpx_tenant_id: channelConfig.tenant_id,
          mpx_device_token: registerResponse.data.device_token, // Use the device token from register_offer response
          mpx_mediator_id: channelConfig.mediator_id,
          mpx_custom_phrase: mpxPublishConfig.connectionPhrase, // Save the connection phrase
          invitation: {
            ...mpxPublishConfig,
            mnemonic: registerResponse.data.mnemonic,
            offer_link: registerResponse.data.offer_link,
          },
        });

        if (result?.config_id) {
          createdConfig.config_id = result.config_id;
          createdConfig.offer_link = registerResponse.data.offer_link;
          createdConfig.mnemonic = registerResponse.data.mnemonic;
        }
      } else {
        // Create A2A or MCP channel using the proper API method
        const result = await apiClient.createChannel({
          ...channelConfig,
          protocol,
        });

        if (result?.config_id) {
          createdConfig.config_id = result.config_id;
          createdConfig.protocol = protocol; // Ensure protocol is set for immediate UI update
        }
      }

      // Add channel to app state immediately (same as delete does with removeChannel)
      actions.addChannel(createdConfig);
      
      // Backend already handles channel creation and dynamic reload
      // No need to call reloadAllChannels() which causes unnecessary gateway restart
      // The channel is already activated and ready to use
      
      onChannelCreated(createdConfig);
    } catch (err: any) {
      console.error('Failed to create channel:', err);
      setError(err.response?.data?.error || err.message || 'Failed to create channel');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="card shadow">
      <div className="card-body">
        <div className="confirm-step">
          <h5 className="mb-4">
            <i className="fas fa-check-circle text-success"></i> Confirm Channel Configuration
          </h5>

          <div className="card mb-4">
            <div className="card-header">
              <h6 className="m-0">
                <span className={`badge ${protocol === 'a2a' ? 'badge-primary' : protocol === 'ap2' ? 'badge-info' : protocol === 'mcp' ? 'badge-success' : 'badge-warning'} mr-2`}>
                  {protocol.toUpperCase()}
                </span>
                Channel Details
              </h6>
            </div>
            <div className="card-body">
              <dl className="row mb-0">
                <dt className="col-sm-3">Name:</dt>
                <dd className="col-sm-9">{channelConfig?.name || 'N/A'}</dd>

                <dt className="col-sm-3">Description:</dt>
                <dd className="col-sm-9">{channelConfig?.description || 'N/A'}</dd>

                {protocol !== 'mpx' && (
                  <>
                    <dt className="col-sm-3">Listen Address:</dt>
                    <dd className="col-sm-9">
                      <code>{channelConfig?.full_listen_url || `${channelConfig?.listen_address}${channelConfig?.route}` || 'N/A'}</code>
                    </dd>

                    <dt className="col-sm-3">Target Endpoint:</dt>
                    <dd className="col-sm-9">
                      <code>{channelConfig?.target_endpoint || 'N/A'}</code>
                    </dd>
                  </>
                )}

                {protocol === 'mpx' && (
                  <>
                    <dt className="col-sm-3">Mediator:</dt>
                    <dd className="col-sm-9">{channelConfig?.mediator_id || 'N/A'}</dd>
                  </>
                )}
              </dl>
            </div>
          </div>

          {protocol === 'mpx' && mpxPublishConfig && (
            <div className="card mb-4">
              <div className="card-header">
                <h6 className="m-0">
                  <i className="fas fa-envelope-open-text"></i> Invitation Details
                </h6>
              </div>
              <div className="card-body">
                <dl className="row mb-0">
                  <dt className="col-sm-3">Connection Phrase:</dt>
                  <dd className="col-sm-9">
                    <code>{mpxPublishConfig.connectionPhrase}</code>
                  </dd>

                  <dt className="col-sm-3">Headline:</dt>
                  <dd className="col-sm-9">{mpxPublishConfig.headline}</dd>

                  <dt className="col-sm-3">Description:</dt>
                  <dd className="col-sm-9">{mpxPublishConfig.description}</dd>

                  <dt className="col-sm-3">Expiry:</dt>
                  <dd className="col-sm-9">
                    {mpxPublishConfig.noExpiry ? (
                      <span className="badge badge-success">Does not expire</span>
                    ) : (
                      <span>{mpxPublishConfig.expiryDate}</span>
                    )}
                  </dd>

                  <dt className="col-sm-3">Usage Limit:</dt>
                  <dd className="col-sm-9">
                    {mpxPublishConfig.noLimit ? (
                      <span className="badge badge-success">Unlimited</span>
                    ) : (
                      <span>{mpxPublishConfig.limitUses} uses</span>
                    )}
                  </dd>
                </dl>
              </div>
            </div>
          )}

          {error && (
            <div className="alert alert-danger">
              <i className="fas fa-exclamation-triangle"></i> {error}
            </div>
          )}

          <div className="alert alert-info">
            <i className="fas fa-info-circle"></i> Review the configuration above. Click "Create
            Channel" to proceed or "Back" to make changes.
          </div>

          <div className="d-flex justify-content-between mt-4">
            <button className="btn btn-secondary" onClick={onBack} disabled={creating}>
              <i className="fas fa-arrow-left"></i> Back
            </button>
            <button
              className="btn btn-success"
              onClick={handleCreate}
              disabled={creating}
            >
              {creating ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i> Creating...
                </>
              ) : (
                <>
                  <i className="fas fa-check"></i> Create Channel
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmStep;
